import type {
  AdminCreditNote,
  AdminInvoice,
  CreateCreditNoteBody,
  CreateInvoiceDraftBody,
  CreditNotePdfUrlResponse,
  InvoiceGenerationStatusResponse,
  InvoicePdfUrlResponse,
  ListInvoicesResponse,
  ListPaymentsResponse,
  RecordInvoicePaymentBody,
  RecordPaymentResponse,
  SendInvoiceBody,
  SendInvoiceResponse,
  UpdateInvoiceDraftBody,
  InvoiceShareLinkResponse,
} from '@fois-utsavvoice/shared';
import { apiRequest } from './client.js';

export function listInvoices(accessToken: string, status?: string): Promise<ListInvoicesResponse> {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiRequest<ListInvoicesResponse>(`/v1/admin/invoices${suffix}`, { accessToken });
}

export function createInvoiceDraft(
  accessToken: string,
  body: CreateInvoiceDraftBody,
): Promise<AdminInvoice> {
  return apiRequest<AdminInvoice>('/v1/admin/invoices', {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });
}

export function getInvoice(accessToken: string, invoiceId: string): Promise<AdminInvoice> {
  return apiRequest<AdminInvoice>(`/v1/admin/invoices/${invoiceId}`, { accessToken });
}

export function updateInvoiceDraft(
  accessToken: string,
  invoiceId: string,
  body: UpdateInvoiceDraftBody,
): Promise<AdminInvoice> {
  return apiRequest<AdminInvoice>(`/v1/admin/invoices/${invoiceId}`, {
    method: 'PATCH',
    accessToken,
    body: JSON.stringify(body),
  });
}

export function deleteInvoiceDraft(accessToken: string, invoiceId: string): Promise<void> {
  return apiRequest<void>(`/v1/admin/invoices/${invoiceId}`, {
    method: 'DELETE',
    accessToken,
  });
}

export function previewInvoicePdf(
  accessToken: string,
  invoiceId: string,
): Promise<InvoicePdfUrlResponse> {
  return apiRequest<InvoicePdfUrlResponse>(`/v1/admin/invoices/${invoiceId}/preview`, {
    method: 'POST',
    accessToken,
  });
}

export function issueInvoice(
  accessToken: string,
  invoiceId: string,
  idempotencyKey: string,
): Promise<AdminInvoice> {
  return apiRequest<AdminInvoice>(`/v1/admin/invoices/${invoiceId}/issue`, {
    method: 'POST',
    accessToken,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export function getInvoiceGenerationStatus(
  accessToken: string,
  invoiceId: string,
): Promise<InvoiceGenerationStatusResponse> {
  return apiRequest<InvoiceGenerationStatusResponse>(
    `/v1/admin/invoices/${invoiceId}/generation-status`,
    { accessToken },
  );
}

export function getInvoicePdfUrl(
  accessToken: string,
  invoiceId: string,
): Promise<InvoicePdfUrlResponse> {
  return apiRequest<InvoicePdfUrlResponse>(`/v1/admin/invoices/${invoiceId}/pdf-url`, {
    accessToken,
  });
}

export function listInvoicePayments(
  accessToken: string,
  invoiceId: string,
): Promise<ListPaymentsResponse> {
  return apiRequest<ListPaymentsResponse>(`/v1/admin/invoices/${invoiceId}/payments`, {
    accessToken,
  });
}

export function recordInvoicePayment(
  accessToken: string,
  invoiceId: string,
  body: RecordInvoicePaymentBody,
  idempotencyKey: string,
): Promise<RecordPaymentResponse> {
  return apiRequest<RecordPaymentResponse>(`/v1/admin/invoices/${invoiceId}/payments`, {
    method: 'POST',
    accessToken,
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });
}

export function voidInvoicePayment(
  accessToken: string,
  paymentId: string,
  reason: string,
  idempotencyKey: string,
): Promise<RecordPaymentResponse> {
  return apiRequest<RecordPaymentResponse>(`/v1/admin/payments/${paymentId}/void`, {
    method: 'POST',
    accessToken,
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify({ reason }),
  });
}

export function voidIssuedInvoice(
  accessToken: string,
  invoiceId: string,
  reason: string,
  idempotencyKey: string,
): Promise<AdminInvoice> {
  return apiRequest<AdminInvoice>(`/v1/admin/invoices/${invoiceId}/void`, {
    method: 'POST',
    accessToken,
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify({ reason }),
  });
}

export function listInvoiceCreditNotes(
  accessToken: string,
  invoiceId: string,
): Promise<{ items: AdminCreditNote[] }> {
  return apiRequest<{ items: AdminCreditNote[] }>(
    `/v1/admin/invoices/${invoiceId}/credit-notes`,
    { accessToken },
  );
}

export function createCreditNote(
  accessToken: string,
  invoiceId: string,
  body: CreateCreditNoteBody,
  idempotencyKey: string,
): Promise<{ creditNote: AdminCreditNote; invoice: AdminInvoice }> {
  return apiRequest<{ creditNote: AdminCreditNote; invoice: AdminInvoice }>(
    `/v1/admin/invoices/${invoiceId}/credit-notes`,
    {
      method: 'POST',
      accessToken,
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(body),
    },
  );
}

export function getCreditNotePdfUrl(
  accessToken: string,
  creditNoteId: string,
): Promise<CreditNotePdfUrlResponse> {
  return apiRequest<CreditNotePdfUrlResponse>(`/v1/admin/credit-notes/${creditNoteId}/pdf-url`, {
    accessToken,
  });
}

export function createInvoiceShareLink(
  accessToken: string,
  invoiceId: string,
  idempotencyKey: string,
): Promise<InvoiceShareLinkResponse> {
  return apiRequest<InvoiceShareLinkResponse>(`/v1/admin/invoices/${invoiceId}/share-link`, {
    method: 'POST',
    accessToken,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

export function sendInvoice(
  accessToken: string,
  invoiceId: string,
  body: SendInvoiceBody,
  idempotencyKey: string,
): Promise<SendInvoiceResponse> {
  return apiRequest<SendInvoiceResponse>(`/v1/admin/invoices/${invoiceId}/send`, {
    method: 'POST',
    accessToken,
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });
}
